"use strict";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { indexedDBService } from "./IndexedDBService";
import { csvParserService } from "./CsvParserService";
class S3DataService {
  constructor() {
    /** Base URL for the S3 CSV API */
    this.baseUrl = "/api/v4/reports/s3_csv";
  }
  // =========================================================================
  // PUBLIC METHODS
  // =========================================================================
  /**
   * Downloads data for the past year for specified entities and record type
   * This is the main entry point for the service
   *
   * @param options Options for downloading past year data
   * @returns Aggregated data from IndexedDB
   */
  downloadPastYearData(options) {
    return __async(this, null, function* () {
      const { entityIds, recordType, onProgress } = options;
      const monthsToDownload = yield this.determineMonthsToDownload(entityIds, recordType);
      const totalToDownload = monthsToDownload.length;
      const progress = {
        total: totalToDownload,
        completed: 0,
        percentage: 0,
        status: "downloading"
      };
      if (onProgress) {
        onProgress(progress);
      }
      try {
        let completedDownloads = 0;
        if (monthsToDownload.length === 0) {
          return yield this.handleNoMonthsToDownload(entityIds, recordType, progress, onProgress);
        }
        const { minYear, maxYear, minMonth, maxMonth } = this.findDateRangeBoundaries(monthsToDownload);
        const urlDataHash = yield this.fetchBatchedPresignedUrls(
          minYear,
          maxYear,
          minMonth,
          maxMonth,
          recordType
        );
        completedDownloads = yield this.processUrlDataHash(
          urlDataHash,
          monthsToDownload,
          recordType,
          progress,
          onProgress
        );
        return yield this.loadAggregatedDataAndUpdateProgress(
          entityIds,
          recordType,
          progress,
          onProgress
        );
      } catch (error) {
        if (onProgress) {
          onProgress(__spreadProps(__spreadValues({}, progress), {
            status: "error",
            error: error instanceof Error ? error.message : "Unknown error occurred"
          }));
        }
        throw error;
      }
    });
  }
  /**
   * Fetches batched presigned URLs for S3 CSV files
   *
   * @param startYear The starting year for the date range
   * @param endYear The ending year for the date range
   * @param startMonth The starting month for the date range
   * @param endMonth The ending month for the date range
   * @param recordType The type of record to retrieve
   * @returns Object containing presigned URLs for each entity and date
   */
  fetchBatchedPresignedUrls(startYear, endYear, startMonth, endMonth, recordType) {
    return __async(this, null, function* () {
      const response = yield fetch(
        `${this.baseUrl}?start_year=${startYear}&end_year=${endYear}&start_month=${startMonth}&end_month=${endMonth}&record_type=${recordType}`
      );
      const data = yield response.json();
      return data;
    });
  }
  /**
   * Downloads and processes a CSV file from a presigned URL
   *
   * @param url The presigned URL to download the CSV from
   * @param recordType The type of record being downloaded
   * @returns Processed CSV data
   */
  downloadAndProcessCsv(url, recordType) {
    return __async(this, null, function* () {
      try {
        const response = yield fetch(url, {
          credentials: "omit",
          // Don't send cookies to S3
          headers: {
            Accept: "text/csv,application/json,text/plain,*/*"
          }
        });
        if (!response.ok) {
          return null;
        }
        const data = yield response.text();
        return csvParserService.parseCSV(data, recordType);
      } catch (error) {
        console.error("Error downloading or processing CSV:", error);
        throw error;
      }
    });
  }
  // =========================================================================
  // PRIVATE METHODS - Data Processing
  // =========================================================================
  /**
   * Processes the URL data hash, downloading and saving data for each month
   *
   * @param urlDataHash Hash of presigned URLs for each entity and month
   * @param monthsToDownload Array of months that need to be downloaded
   * @param recordType The type of record to download
   * @param progress Current progress object
   * @param onProgress Optional callback for progress updates
   * @returns Number of completed downloads
   */
  processUrlDataHash(urlDataHash, monthsToDownload, recordType, progress, onProgress) {
    return __async(this, null, function* () {
      let completedDownloads = 0;
      const totalToDownload = monthsToDownload.length;
      for (const [entityId, monthlyUrlKeyValuePair] of Object.entries(urlDataHash)) {
        for (const [yearMonthKey, url] of Object.entries(monthlyUrlKeyValuePair)) {
          const [year, month] = yearMonthKey.split("-");
          const entityIdNum = parseInt(entityId);
          const yearNum = parseInt(year);
          const monthNum = parseInt(month);
          const shouldDownload = monthsToDownload.some(
            (item) => item.entityId === entityIdNum && item.year === yearNum && item.month === monthNum
          );
          if (!shouldDownload) {
            continue;
          }
          const data = yield this.downloadAndProcessCsv(url, recordType);
          yield indexedDBService.saveChartData(
            entityIdNum,
            yearNum,
            monthNum,
            recordType,
            data ? data : {
              reportNotFound: true
            },
            recordType
          );
          completedDownloads++;
          progress.completed = completedDownloads;
          progress.percentage = totalToDownload > 0 ? Math.round(completedDownloads / totalToDownload * 100) : 100;
          if (onProgress) {
            onProgress(__spreadValues({}, progress));
          }
        }
      }
      return completedDownloads;
    });
  }
  /**
   * Loads aggregated data from IndexedDB and updates progress
   *
   * @param entityIds Array of entity IDs to get data for
   * @param recordType The type of record to get data for
   * @param progress Current progress object
   * @param onProgress Optional callback for progress updates
   * @returns Aggregated data from IndexedDB
   */
  loadAggregatedDataAndUpdateProgress(entityIds, recordType, progress, onProgress) {
    return __async(this, null, function* () {
      if (onProgress) {
        onProgress(__spreadProps(__spreadValues({}, progress), {
          status: "processing"
        }));
      }
      const allData = yield indexedDBService.getAggregatedData(entityIds, recordType);
      if (onProgress) {
        onProgress(__spreadProps(__spreadValues({}, progress), {
          status: "complete"
        }));
      }
      return allData;
    });
  }
  /**
   * Handles the case when there are no months to download
   *
   * @param entityIds Array of entity IDs to get data for
   * @param recordType The type of record to get data for
   * @param progress Current progress object
   * @param onProgress Optional callback for progress updates
   * @returns Aggregated data from IndexedDB
   */
  handleNoMonthsToDownload(entityIds, recordType, progress, onProgress) {
    return __async(this, null, function* () {
      if (onProgress) {
        onProgress(__spreadProps(__spreadValues({}, progress), {
          status: "processing"
        }));
      }
      const allData = yield indexedDBService.getAggregatedData(entityIds, recordType);
      if (onProgress) {
        onProgress(__spreadProps(__spreadValues({}, progress), {
          status: "complete"
        }));
      }
      return allData;
    });
  }
  // =========================================================================
  // PRIVATE METHODS - Data Analysis
  // =========================================================================
  /**
   * Determines which months need to be downloaded for each entity
   *
   * @param entityIds Array of entity IDs to check
   * @param recordType The type of record to check
   * @returns Array of objects containing entityId, year, and month for data that needs to be downloaded
   */
  determineMonthsToDownload(entityIds, recordType) {
    return __async(this, null, function* () {
      const monthsToDownload = [];
      const currentDate = /* @__PURE__ */ new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      for (const entityId of entityIds) {
        for (let i = 0; i < 12; i++) {
          let month = currentMonth - i;
          let year = currentYear;
          if (month <= 0) {
            month += 12;
            year -= 1;
          }
          const existingData = yield indexedDBService.getChartData(entityId, year, month, recordType);
          if (!existingData) {
            monthsToDownload.push({ entityId, year, month });
          }
        }
      }
      return monthsToDownload;
    });
  }
  /**
   * Finds the minimum and maximum year-month combinations from the months to download
   *
   * @param monthsToDownload Array of objects containing entityId, year, and month
   * @returns Object containing minYear, maxYear, minMonth, and maxMonth
   */
  findDateRangeBoundaries(monthsToDownload) {
    let minYear = Number.MAX_SAFE_INTEGER;
    let maxYear = Number.MIN_SAFE_INTEGER;
    let minMonth = Number.MAX_SAFE_INTEGER;
    let maxMonth = Number.MIN_SAFE_INTEGER;
    if (monthsToDownload.length > 0) {
      minYear = maxYear = monthsToDownload[0].year;
      minMonth = maxMonth = monthsToDownload[0].month;
    }
    for (const { year, month } of monthsToDownload) {
      if (year < minYear || year === minYear && month < minMonth) {
        minYear = year;
        minMonth = month;
      }
      if (year > maxYear || year === maxYear && month > maxMonth) {
        maxYear = year;
        maxMonth = month;
      }
    }
    return { minYear, maxYear, minMonth, maxMonth };
  }
}
export const s3DataService = new S3DataService();
